import { extractOdomPosition } from "../state/gps-event-coordinator.js";
//#region ../src/ar/depth-sampler.ts
const DEFAULT_CONFIG = {
	intervalMs: 1e3,
	gridSize: 3,
	unavailabilityThresholdMs: 5e3
};
/**
* Samples sparse depth points from WebXR depth sensing.
*
* Usage:
* ```ts
* const sampler = new DepthSampler({
*   onSampleCaptured: (sample) => saveSample(sample),
*   getCurrentPose: () => arSession.getCurrentPose(),
* });
* sampler.start();
* // In frame loop:
* sampler.onFrame(timestamp, depthInfo);
* ```
*/
var DepthSampler = class {
	callbacks;
	config;
	running = false;
	sampleCount = 0;
	lastSampleTime = -Infinity;
	/** Timestamp when sampling started (for unavailability detection) */
	startTime = -Infinity;
	/** Whether we've ever received valid depth data */
	depthReceived = false;
	/** Whether we've already fired the unavailable callback */
	unavailableCallbackFired = false;
	constructor(callbacks, config) {
		this.callbacks = callbacks;
		this.config = {
			...DEFAULT_CONFIG,
			...config
		};
	}
	/**
	* Start depth sampling.
	*/
	start() {
		this.running = true;
		this.sampleCount = 0;
		this.lastSampleTime = -Infinity;
		this.startTime = performance.now();
		this.depthReceived = false;
		this.unavailableCallbackFired = false;
	}
	/**
	* Stop depth sampling.
	*/
	stop() {
		this.running = false;
	}
	/**
	* Check if sampler is currently running.
	*/
	isRunning() {
		return this.running;
	}
	/**
	* Get the number of samples captured since start.
	*/
	getSampleCount() {
		return this.sampleCount;
	}
	/**
	* Get the current configuration.
	*/
	getConfig() {
		return { ...this.config };
	}
	/**
	* Called each frame with depth information.
	*
	* @param timestamp - Current frame timestamp in milliseconds
	* @param depthInfo - WebXR depth information, or null if unavailable
	*/
	onFrame(timestamp, depthInfo) {
		if (!this.running) return;
		if (!depthInfo) {
			this.checkDepthUnavailability();
			return;
		}
		this.depthReceived = true;
		if (timestamp - this.lastSampleTime < this.config.intervalMs) return;
		const pose = this.callbacks.getCurrentPose();
		if (!pose) return;
		const points = this.sampleGrid(depthInfo);
		const sample = {
			timestamp: performance.timeOrigin + timestamp,
			cameraPos: extractOdomPosition(pose),
			cameraRot: [
				pose.orientation.x,
				pose.orientation.y,
				pose.orientation.z,
				pose.orientation.w
			],
			points
		};
		this.lastSampleTime = timestamp;
		this.sampleCount++;
		this.callbacks.onSampleCaptured(sample);
	}
	/**
	* Sample a grid of depth points from the depth buffer.
	*/
	sampleGrid(depthInfo) {
		const points = [];
		const gridSize = this.config.gridSize;
		for (let row = 0; row < gridSize; row++) for (let col = 0; col < gridSize; col++) {
			const screenX = (col + 1) / (gridSize + 1);
			const screenY = (row + 1) / (gridSize + 1);
			const depthM = depthInfo.getDepthInMeters(screenX, screenY);
			points.push({
				screenX,
				screenY,
				depthM
			});
		}
		return points;
	}
	/**
	* Check if depth has been unavailable for longer than the threshold.
	* If so, fire the onDepthUnavailable callback (once).
	*/
	checkDepthUnavailability() {
		if (this.depthReceived || this.unavailableCallbackFired) return;
		if (!this.callbacks.onDepthUnavailable) return;
		if (performance.now() - this.startTime >= this.config.unavailabilityThresholdMs) {
			this.unavailableCallbackFired = true;
			this.callbacks.onDepthUnavailable();
		}
	}
	/**
	* Check if depth data has ever been received.
	* Useful for testing and status display.
	*/
	hasReceivedDepth() {
		return this.depthReceived;
	}
};
//#endregion
export { DepthSampler };
